import React, { useEffect, useState } from 'react';
import 'react-leaflet-fullscreen/dist/styles.css';
import FullscreenControl from 'react-leaflet-fullscreen';
import { Drawer } from 'antd';
import {
  GoogleMap,
  Marker,
  withGoogleMap,
  withScriptjs,
} from 'react-google-maps';
import CarIcon from '../../../../assets/automobile.svg';

import * as Leaflet from 'react-leaflet';

import { getClient } from '../../../../apollo';
import {
  useGetAllDriversLocationQuery,
  useGetAllVehiclesLocationQuery,
  useOnAnyDriverLocationUpdateSubscription,
} from '../../../../graphql/datamodel.gen';
import { Button } from 'antd/es';
import { Icon as LeafletIcon, DivIcon as LeafletDivIcon } from 'leaflet';
import { DriftMarker } from 'leaflet-drift-marker';

interface Props {
  visible: boolean;
  onClose: () => any;
}

const TILE_MAP_URL = `https://api.mapbox.com/styles/v1/${process.env.REACT_APP_MAPBOX_USERNAME}/${process.env.REACT_APP_MAPBOX_MAP_ID}/tiles/{z}/{x}/{y}?access_token=${process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}`;

const CarIconLeaflet = new LeafletIcon({
  iconUrl: CarIcon,
  iconSize: [35, 35],
});

const MyMapComponent = withScriptjs(
  withGoogleMap((props: any) => {
    return (
      <GoogleMap
        defaultZoom={12}
        defaultCenter={{
          lat: 41.39701,
          lng: 2.169457,
        }}
      >
        {props.markers.map((marker) => {
          return (
            <Marker
              key={marker.id}
              icon={{
                url: CarIcon,
                // @ts-ignore
                scaledSize: { width: 45, height: 45 },
              }}
              position={{
                lat: parseFloat(marker.lat),
                lng: parseFloat(marker.lon),
              }}
            />
          );
        })}
      </GoogleMap>
    );
  }),
);

interface MapViewProps {
  driversLocations: { id: string; lat: number; lon: number }[];
}

const LeaftLetView: React.FC<MapViewProps> = (props: MapViewProps) => {
  return (
    <div
      id={'container'}
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        zIndex: 0,
      }}
    >
      <Leaflet.Map
        preferCanvas={true}
        center={[41.39701, 2.169457]}
        zoom={12}
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: '100%',
          height: '95vh',
        }}
      >
        <Leaflet.TileLayer
          updateWhenZooming={true}
          detectRetina={false}
          maxZoom={19}
          keepBuffer={16}
          url={TILE_MAP_URL}
          attribution='© <a href="https://apps.mapbox.com/feedback/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        {props.driversLocations.map((loc) => (
          <DriftMarker
            key={loc.id}
            position={[loc.lat, loc.lon]}
            icon={CarIconLeaflet}
            duration={200}
          />
          //
          // <Leaflet.Marker
          //   key={loc.id}
          //   position={[loc.lat, loc.lon]}
          //   icon={CarIconLeaflet}
          //
          // />
        ))}
        <FullscreenControl position="topleft" />
      </Leaflet.Map>
    </div>
  );
};

const MapView: React.FC<MapViewProps> = (props: MapViewProps) => {
  return <LeaftLetView driversLocations={props.driversLocations} />;
};

const MapListener = () => {
  const [driversLocation, setDriversLocation] = useState<
    {
      id: string;
      lat: number;
      lon: number;
    }[]
  >([]);
  const [vehiclesLocation, setVehiclesLocation] = useState<
    {
      id: string;
      lat: number;
      lon: number;
    }[]
  >([]);

  const { loading, data, error } = useGetAllDriversLocationQuery({
    client: getClient(),
    fetchPolicy: 'network-only',
  });

  const { data: vehiclesData } = useGetAllVehiclesLocationQuery({
    client: getClient(),
    fetchPolicy: 'network-only',
    pollInterval: 1000,
  });

  useEffect(() => {
    if (data?.drivers) {
      // @ts-ignore
      const mappedData: { id: string; lat: number; lon: number }[] = (
        data?.drivers || []
      )
        .filter((driver) => driver.location)
        .map((driver) => {
          return {
            id: driver.id,
            lat: Number.parseFloat(driver.location.latitude),
            lon: Number.parseFloat(driver.location.longitude),
          };
        });

      setDriversLocation([...mappedData]);
    }

    if (vehiclesData?.vehicles) {
      // @ts-ignore
      const mappedData: { id: string; lat: number; lon: number }[] = (
        vehiclesData?.vehicles || []
      )
        .filter((vehicle) => vehicle.location)
        .map((vehicle) => {
          return {
            id: vehicle.id,
            lat: Number.parseFloat(vehicle.location.latitude),
            lon: Number.parseFloat(vehicle.location.longitude),
          };
        });

      setVehiclesLocation([...mappedData]);
    }
  }, [data, vehiclesData]);

  useOnAnyDriverLocationUpdateSubscription({
    client: getClient(),
    shouldResubscribe: true,
    onSubscriptionData: ({ subscriptionData }) => {
      const driverLoc = subscriptionData.data.onAnyDriverLocationUpdate;
      const existingLocation = driversLocation.find(
        (driver) => driver.id === driverLoc.id,
      );

      let updatedDriverLocationsArray = [];

      if (existingLocation) {
        updatedDriverLocationsArray = [
          ...driversLocation.filter(
            (driverLocc) => driverLocc.id !== driverLoc.id,
          ),
          {
            id: driverLoc.id,
            lat: driverLoc.location.latitude,
            lon: driverLoc.location.longitude,
          },
        ];
      } else {
        updatedDriverLocationsArray = [
          ...driversLocation,
          {
            id: driverLoc.id,
            lat: driverLoc.location.latitude,
            lon: driverLoc.location.longitude,
          },
        ];
      }

      setDriversLocation(updatedDriverLocationsArray);
    },
  });

  return (
    <MapView driversLocations={[...driversLocation, ...vehiclesLocation]} />
  );
};

const GlobalMap: React.FC<Props> = (props: Props) => {
  return (
    <Drawer
      placement={'bottom'}
      height={'95%'}
      closable={true}
      destroyOnClose={false}
      visible={props.visible}
      onClose={props.onClose}
      drawerStyle={{
        overflow: 'hidden',
      }}
      bodyStyle={{
        overflow: 'hidden',
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: '25px',
        paddingBottom: 0,
      }}
    >
      <Button
        icon={'close'}
        className={'ant-drawer-close'}
        style={{ color: 'white', zIndex: 2 }}
      />
      {props.visible && <MapListener />}
    </Drawer>
  );
};

export default GlobalMap;
